import type { RoadmapNode, RoadmapNodeKind } from "../api/roadmap-service";

/**
 * The roadmap as of one level: a node is kept when it appears at or below the level. A parent appears with its
 * lowest child, so whatever is kept is still connected to the root.
 */
export function treeAtLevel(node: RoadmapNode, level: number): RoadmapNode | undefined {
    if (node.level > level) return undefined;

    const children = node.children
        .map((child) => treeAtLevel(child, level))
        .filter((child): child is RoadmapNode => child !== undefined);

    return { ...node, children };
}

/** How many nodes of a kind the tree holds, e.g. the lessons expected at the selected level. */
export function countNodes(node: RoadmapNode, kind: RoadmapNodeKind): number {
    const own = node.kind === kind ? 1 : 0;

    return own + node.children.reduce((sum, child) => sum + countNodes(child, kind), 0);
}

/** How many alternatives of a choice are expected at a level; a visible choice always has a pick at or below it. */
export function pickAt(choice: RoadmapNode, level: number): number {
    return choice.picks.filter((pick) => pick.level <= level).at(-1)?.count ?? 0;
}

/** What a choice expects, worded for the map: `One of`, `Both`, `All of` or e.g. `2 of 3`. */
export function choiceRule(count: number, alternatives: number): string {
    if (count === alternatives) return alternatives === 2 ? 'Both' : 'All of';
    if (count === 1) return 'One of';

    return `${ count } of ${ alternatives }`;
}

/** A node is identified by its position, because a planned node may carry no reference at all. */
export function childKey(parentKey: string, index: number): string {
    return `${ parentKey }/${ index }`;
}

/** A last node of the roadmap file, whose children are the lessons read from the catalogue. */
export function holdsLessons(node: RoadmapNode): boolean {
    return node.children.some((child) => child.kind === 'LESSON');
}

/** Keys of every node holding lessons, so all lessons of the map can be folded at once. */
export function keysHoldingLessons(node: RoadmapNode, key = 'root'): string[] {
    const own = holdsLessons(node) ? [ key ] : [];

    return own.concat(node.children.flatMap((child, index) => keysHoldingLessons(child, childKey(key, index))));
}
